import React, {useState, useEffect, useContext} from 'react';
import { SystemContext } from './SystemBuilder';
import Dropdown from '../Assets/Dropdown';
import Input from '../Assets/Input';
import BaseballIcon from '@assets/images/icons/sports/baseball.svg?react';
import FootballIcon from '@assets/images/icons/sports/football.svg?react';
import BasketballIcon from '@assets/images/icons/sports/basketball.svg?react';

const SystemInfo = (props) => {
    const sysContext = useContext(SystemContext)
    const [errs, setErrs] = useState([]);
    const [betOptions, setBetOptions] = useState([]);

    const sportOptions = [
        { value: "mlb", label: "MLB", icon: <BaseballIcon className="h-5 w-5" /> },
        { value: "nfl", label: "NFL", icon: <FootballIcon className="h-5 w-5" /> },
        { value: "nba", label: "NBA", icon: <BasketballIcon className="h-5 w-5" /> }
    ];

    const systemTypes = [
        { id: 'private', title: 'Private' },
        { id: 'public', title: 'Public' }
    ];

    // Run when sport changes
    useEffect(() => {
        if(sysContext.sport !== ""){
            const regBets = [
                { value: "home.ml", label: "Home Moneyline" },
                { value: "away.ml", label: "Away Moneyline" },
                { value: "over", label: "Over" },
                { value: "under", label: "Under" }
            ];

            const mlbBets = [
                { value: "home.runline", label: "Home Runline" },
                { value: "away.runline", label: "Away Runline" }
            ];

            const nflBets = [
                { value: "home.spread", label: "Home Spread" },
                { value: "away.spread", label: "Away Spread" }
            ];

            const nbaBets = [
                { value: "home.spread", label: "Home Spread" },
                { value: "away.spread", label: "Away Spread" }
            ];
            
            // set bets state variable 
            let tmpBets = [...regBets];
            if(sysContext.sport === "mlb"){
                tmpBets = [...tmpBets, ...mlbBets];
            }else if(sysContext.sport === "nfl"){
                tmpBets = [...tmpBets, ...nflBets];
            }else if(sysContext.sport === "nba"){
                tmpBets = [...tmpBets, ...nbaBets];
            }

            // Sort by label
            tmpBets.sort((a, b) => a.label.localeCompare(b.label));
            setBetOptions(tmpBets);
        } else {
            setBetOptions([]);
        }
    },[sysContext.sport]);

    // run whenever errs changes
    useEffect(()=>{
        // get all elements previously in error state and remove class
        let prevErrs = document.getElementsByClassName("err");
        while(prevErrs.length){
            prevErrs[0].classList.remove("err");
        };
        // add err class based on updated errs
        errs.forEach(err=>{
            const elem = document.getElementById(err);
            if(elem){
                elem.classList.add("err");
            };
        });
    },[errs]);

    const validateForm = async (e) => {
        e.preventDefault();
        const tmpErrs = await props.validateSystemInfo();
        setErrs(tmpErrs);
        if(tmpErrs.length === 0){
            sysContext.setStep(2);
        };
    };

    // Clear name error when name is updated
    useEffect(() => {
        if (errs.includes("name") && sysContext.name !== "") {
            setErrs(prev => prev.filter(err => err !== "name"));
        }
    }, [sysContext.name, errs]);
    
    // Clear sport error when sport is updated
    useEffect(() => {
        if (errs.includes("sport") && sysContext.sport !== "") {
            setErrs(prev => prev.filter(err => err !== "sport"));
        }
    }, [sysContext.sport, errs]);

    // Clear bet error when bet is updated
    useEffect(() => {
        if (errs.includes("bet") && sysContext.bet !== "") {
            setErrs(prev => prev.filter(err => err !== "bet"));
        }
    }, [sysContext.bet, errs]);
    
    return (
        <div className="space-y-6">
            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                <h2 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">System Information</h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Enter the basic information for your betting system.
                </p>
            </div>

            <form className="space-y-6" onSubmit={validateForm}>
                {props.pageType === "new" && (
                    <div className="space-y-2">
                        <fieldset>
                            <legend className="text-sm font-medium text-gray-700 dark:text-gray-200">System Type</legend>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Choose whether your system will be private or public.</p>
                            <div className="mt-4 flex items-center space-x-6">
                                {systemTypes.map((type) => (
                                    <div key={type.id} className="flex items-center">
                                        <input
                                            id={type.id}
                                            name="systemType"
                                            type="radio"
                                            checked={sysContext.sysType === type.id}
                                            onChange={() => props.setSysType(type.id)}
                                            className="relative h-4 w-4 appearance-none rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 before:absolute before:inset-1 before:rounded-full before:bg-white dark:before:bg-gray-800 not-checked:before:hidden checked:border-brand-red-500 dark:checked:border-brand-red-400 checked:bg-brand-red-500 dark:checked:bg-brand-red-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500 dark:focus-visible:outline-brand-red-400 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400"
                                        />
                                        <label htmlFor={type.id} className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                                            {type.title}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                    </div>
                )}

                <div className="space-y-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        System Name
                    </label>
                    <Input
                        type="text"
                        id="name"
                        value={sysContext.name}
                        onChange={(e) => props.setName(e.target.value)}
                        placeholder="Enter system name"
                        className={errs.includes("name") ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-brand-red-500 focus:ring-brand-red-500 border-red-500" : ""}
                    />
                    {errs.includes("name") && (
                        <p className="mt-1 text-sm text-red-500">System name is required</p>
                    )}
                </div>

                <div className="space-y-2">
                    <label htmlFor="sport" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                        Sport
                    </label>
                    <Dropdown
                        id="sport"
                        title="Select a sport"
                        items={sportOptions}
                        activeItem={sysContext.sport}
                        onSelect={props.setSport}
                        size="md"
                        variant="default"
                        className={errs.includes("sport") ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-brand-red-500 focus:ring-brand-red-500 border-red-500" : ""}
                    />
                    {errs.includes("sport") && (
                        <p className="mt-1 text-sm text-red-500">Sport selection is required</p>
                    )}
                </div>

                {sysContext.sport !== "" && (
                    <div className="space-y-2">
                        <label htmlFor="bet" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                            Bet Type
                        </label>
                        <Dropdown
                            id="bet"
                            title="Select a bet type"
                            items={betOptions}
                            activeItem={sysContext.bet}
                            onSelect={props.setBet}
                            size="md"
                            variant="default"
                            className={errs.includes("bet") ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-brand-red-500 focus:ring-brand-red-500 border-red-500" : ""}
                        />
                        {errs.includes("bet") && (
                            <p className="mt-1 text-sm text-red-500">Bet type selection is required</p>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={props.navToSystems}
                        className="rounded-md bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                    >
                        Next
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SystemInfo;